import { Box, Button, Flex, Grid, Heading, Image, Text } from '@chakra-ui/react';
import NextImage from 'next/image';

import AboutDivider from '@/components/AboutUs/images/about/divider.png';
import TopBgClosing from '@/components/AboutUs/images/hero/top-bg-closing.png';
import TopBgClosingMobile from '@/components/AboutUs/images/hero/top-bg-closing-mobile.svg';
import TopBgClosingWide from '@/components/AboutUs/images/hero/top-bg-closing-wide.svg';
import TopBgOpening from '@/components/AboutUs/images/hero/top-bg-opening.png';
import TopBgOpeningMobile from '@/components/AboutUs/images/hero/top-bg-opening-mobile.png';
import TopBgOpeningWide from '@/components/AboutUs/images/hero/top-bg-opening-wide.png';
import MapBg from '@/components/AboutUs/images/map-bg.png';
import openRoles from '@/components/AboutUs/open-roles.json';
import { Fade } from '@/components/Common/Animations/FramerMotion/Appear/Fade';
import { TextBlurFadeEffect } from '@/components/Common/Animations/ReactTextAnimate/TextBlurFadeEffect';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { terrain } from '@/utils/fonts';

import { Benefits } from './Benefits';
import { LazyAboutUsHeroAnimation } from './components/HeroAnimation';
import NoOpenRoleBadge from './images/no-open-roles.svg';

type OpenRole = {
  base_salary: string;
  description?: string;
  location: string;
  location_icon?: string;
  title: string;
  type: string;
  url: string;
};

export const AboutUsContent = () => {
  const { isDesktop, isPageLoaded } = useIsDesktop();

  return (
    <>
      <Flex
        flexDir="column"
        gap="60px"
        justifyContent="center"
        minHeight={{ base: 'unset', md: '632px' }}
        pb={{ base: 0, md: '50px' }}
        position="relative"
        pt={{ base: 0, md: '60px' }}
      >
        {isPageLoaded &&
          (isDesktop ? (
            <Box
              bottom="0"
              left="0"
              position="absolute"
              right={{ '2xl': '0', base: '-10%' }}
              top="20%"
              transform={{ '2xl': 'scale(1.25)', base: 'scale(1.5)' }}
            >
              <LazyAboutUsHeroAnimation />
            </Box>
          ) : (
            <Box as="picture" mx="auto" pointerEvents="none">
              <source media="(min-width: 1920px)" srcSet={TopBgOpeningWide.src} />
              <source media="(min-width: 768px)" srcSet={TopBgOpening.src} />
              <Image alt="" src={TopBgOpeningMobile.src} />
            </Box>
          ))}

        <Heading
          alignSelf="center"
          as="h1"
          color="teal.500"
          fontFamily={terrain.style.fontFamily}
          fontSize={{ base: '50px', md: '96px' }}
          fontWeight={300}
          letterSpacing={{ base: '-1.5px', md: '-3px' }}
          lineHeight={{ base: '45px', md: '70px' }}
          maxWidth={{ base: '300px', md: '600px' }}
          mt={{ base: '32px', md: '0' }}
          textAlign="center"
        >
          <TextBlurFadeEffect text="Work smarter with us" />
        </Heading>

        {isPageLoaded && !isDesktop && (
          <Box as="picture" mx="auto" pointerEvents="none" width="100%">
            <source media="(min-width: 1920px)" srcSet={TopBgClosingWide} />
            <source media="(min-width: 768px)" srcSet={TopBgClosing.src} />
            <Image alt="" src={TopBgClosingMobile} width="100%" />
          </Box>
        )}
      </Flex>

      <Box pb={{ base: '60px', md: '126px' }} pt={{ base: '60px', md: '116px' }} px="24px">
        <Flex
          bgColor="white"
          border="1px solid"
          borderColor="teal.100"
          borderRadius="20px"
          boxShadow="md"
          flexDir="column"
          gap={{ base: '30px', md: '50px' }}
          maxW={{ base: '100%', md: '880px', xl: '1024px' }}
          mx="auto"
          p={{ base: '24px', md: '72px' }}
        >
          <Heading
            as={Fade}
            color="teal.400"
            fontFamily={terrain.style.fontFamily}
            fontSize={{ base: '37px', md: '44px' }}
            fontWeight={500}
            letterSpacing="-0.88px"
          >
            About Us
          </Heading>

          <Text
            as={Fade}
            color="gray.700"
            delay={1.4}
            duration={1}
            fontSize={{ base: '20px', md: '30px' }}
            lineHeight={{ base: '28px', md: '38px' }}
          >
            Neuron rekindles your love for sales prospecting with real-time, accurate contact data
            in a beautifully simple browser experience.
            <br />
            <br />
            The world’s top sales teams use Neuron to crush their sales targets.
          </Text>

          <Box as="picture" mx={{ base: '-24px', md: '-72px' }} pointerEvents="none">
            <Image alt="" loading="lazy" src={AboutDivider.src} />
          </Box>

          <Heading
            as={Fade}
            color="teal.400"
            fontFamily={terrain.style.fontFamily}
            fontSize={{ base: '37px', md: '44px' }}
            fontWeight={500}
            letterSpacing="-0.88px"
          >
            Our Story
          </Heading>

          <Text
            as={Fade}
            color="gray.700"
            delay={1.4}
            duration={1}
            fontSize={{ base: '16px', md: '22px' }}
            lineHeight={{ base: '22px', md: '30px' }}
          >
            Founded in 2014 in Waterloo, Canada, by a group of engineers frustrated with the
            existing contact data tools on the market, Neuron was built to solve our own problem. We
            needed an easy way to find new sales leads with accurate contact data. Since then,
            we&apos;ve become the go-to tool for thousands of sales professionals who use Neuron
            every day to hit their quotas.
            <br />
            <br />
            Our mission is to build a timeless, must-have product that sales professionals around
            the world love using.
            <br />
            <br />
            Today, we are a small but efficient team that moves quickly. We’re built on the values
            of ownership, autonomy, and doing your best work. Fully bootstrapped and profitable,
            we’re proud to have no external investors or VCs, allowing us to stay true to our
            culture and mission.
            <br />
            <br />
            To help you do great work, we prioritize work-life balance — we don&apos;t work weekends
            and take ample time off. We also proactively remove distractions that hinder your focus.
            This means no traditional managers, no wasteful meetings, and plenty of room for
            personal and professional growth.
          </Text>
        </Flex>
      </Box>

      <Box bg="teal.600" pb={{ base: '64px', md: '96px' }} pt={{ base: '64px', md: '96px' }}>
        <Box
          maxWidth={{ base: '100%', md: '768px' }}
          mb="64px"
          mx="auto"
          px={{ base: '24px', md: '32px' }}
        >
          <Heading
            as="h3"
            color="yellow.400"
            fontFamily={terrain.style.fontFamily}
            fontSize={{ base: '37px', md: '64px' }}
            fontWeight="normal"
            letterSpacing={{ base: '-0.75px', md: '-1.25px' }}
            lineHeight={{ base: '40px', md: '65px' }}
            textAlign="center"
          >
            <TextBlurFadeEffect text="Remote-First, Always" />
          </Heading>
          <Text
            color="white"
            fontSize={{ base: '16px', md: '18px' }}
            lineHeight="22px"
            mt={{ base: '12px', md: '20px' }}
            textAlign="center"
          >
            Work from anywhere. We’re a 100% distributed team, embracing diverse cultures,
            countries, and time zones.
          </Text>
        </Box>

        <Image
          alt=""
          loading="lazy"
          maxW={{ base: '100%', lg: '1024px' }}
          mx="auto"
          pointerEvents="none"
          src={MapBg.src}
          userSelect="none"
        />
      </Box>

      <Box bgColor="white" px={{ base: '24px', md: '32px' }} py={{ base: '66px', md: '96px' }}>
        <Heading
          as="h3"
          color="black"
          fontFamily={terrain.style.fontFamily}
          fontSize={{ base: '37px', md: '64px' }}
          fontWeight={400}
          letterSpacing={{ base: '-0.75px', md: '-1.25px' }}
          lineHeight={{ base: '44px', md: '64px' }}
          maxW={{ base: '100%', md: '860px' }}
          mb={{ base: '44px', md: '64px' }}
          mx="auto"
        >
          <TextBlurFadeEffect text="Benefits and Perks" />
        </Heading>

        <Grid
          as={Fade}
          gridColumnGap="32px"
          gridRowGap="64px"
          gridTemplateColumns={{
            '2xl': 'repeat(3, 1fr)',
            base: '1fr',
            md: 'repeat(2, 1fr)',
          }}
          maxW={{ base: '100%', md: '860px' }}
          mx="auto"
        >
          <Benefits />
        </Grid>
      </Box>

      <Box px={{ base: '24px', md: '32px' }} py={{ base: '66px', md: '96px' }}>
        <Heading
          as="h3"
          color="black"
          fontFamily={terrain.style.fontFamily}
          fontSize={{ base: '37px', md: '64px' }}
          fontWeight={400}
          letterSpacing={{ base: '-0.75px', md: '-1.25px' }}
          lineHeight={{ base: '44px', md: '64px' }}
          mb={{ base: '44px', md: '64px' }}
          textAlign="center"
        >
          <TextBlurFadeEffect text="Open Roles" />
        </Heading>

        {openRoles.length > 0 ? (
          <Flex as={Fade} flexDir="column" gap="24px">
            {openRoles.map((job: OpenRole) => {
              return (
                <Box
                  bgColor="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="16px"
                  boxShadow="0px 4px 9px -2px rgba(95, 183, 202, 0.14), 0px 2px 5px -2px rgba(95, 183, 202, 0.08)"
                  key={job.url}
                  mx="auto"
                  pb="28px"
                  pt="24px"
                  px="24px"
                  width={{ base: '100%', lg: '800px' }}
                >
                  <Text color="gray.900" fontSize="18px" fontWeight={600}>
                    {job.title}
                  </Text>
                  {job.description && (
                    <Text color="gray.600" fontSize="16px" mt="8px">
                      {job.description}
                    </Text>
                  )}

                  <Flex
                    alignItems={{ base: 'baseline', md: 'flex-end' }}
                    flexDir={{ base: 'column', md: 'row' }}
                    gap="24px"
                    justifyContent="space-between"
                    mt={{ base: '24px', md: '16px' }}
                  >
                    <Flex
                      alignItems={{ base: 'stretch', md: 'center' }}
                      flexDir={{ base: 'column', md: 'row' }}
                      gap="16px"
                      minHeight="24px"
                      width="100%"
                    >
                      <Text display="inline-flex">
                        {job.location_icon && (
                          <NextImage
                            alt=""
                            height="16"
                            src={job.location_icon}
                            style={{
                              marginRight: '8px',
                            }}
                            width="16"
                          />
                        )}
                        {job.location}
                      </Text>
                      <Text
                        borderBottom={{ base: '1px solid #D4D4D4', md: 'none' }}
                        borderLeft={{ base: 'none', md: '1px solid #D4D4D4' }}
                        borderRight={{ base: 'none', md: '1px solid #D4D4D4' }}
                        borderTop={{ base: '1px solid #D4D4D4', md: 'none' }}
                        px={{ base: 0, md: '16px' }}
                        py={{ base: '16px', md: 0 }}
                      >
                        {job.type}
                      </Text>
                      <Text>{job.base_salary}</Text>
                    </Flex>

                    <Button
                      as="a"
                      bgColor="teal.500"
                      boxShadow="sm"
                      href={job.url}
                      size="xl"
                      target="_blank"
                    >
                      Apply
                    </Button>
                  </Flex>
                </Box>
              );
            })}
          </Flex>
        ) : (
          <Box textAlign="center">
            <Text
              color="teal.600"
              fontSize={{ base: '20px', md: '30px' }}
              mb={{ base: '44px', md: '64px' }}
            >
              Sorry, there are no open roles at the moment
            </Text>

            <Box as="picture">
              <Image alt="" mx="auto" src={NoOpenRoleBadge} />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};
